/**
 * Team Insights — the numbers behind the redesigned Patient Balances cards (pure, import-free).
 *
 * The old pie charts draw from a chart.js `chartData` ({ labels, datasets: [{ data }] }) that
 * TeamInsights.vue builds with makeChartData(). The new cards take that SAME object, so nothing is
 * recounted: every total here is a sum of numbers the classic page already shows. This file only
 * turns them into tiles, petals and bars. Keep it import-free — cypress/node-tests/team-insights-outcome-model.test.js
 * executes this exact file.
 *
 * ── Outcomes vary per site. The follow-up statuses come from the site's own list (Patient Balances: TaskTypeID 7),
 * so there can be one outcome or twenty. The first MAX_OUTCOMES keep their own colour, fixed by their position in the
 * site's list (blue, purple, orange — the first three of the Mebius look — then five more hues); anything past
 * that folds into one grey "Other". Colour follows the outcome, never its rank, and every colour sits beside its
 * label (the first three fail the colour-blind check on their own — see the pilot notes).
 */

export const PETALS = 50;
export const MAX_OUTCOMES = 8;

// [colour, pale tint]. Positions 0–2 are the Mebius blue / purple / orange; 3–7 are the app's own chart hues.
export const OUTCOME_COLORS = [
  { color: '#0a7cf5', tint: '#e7f2fe' },
  { color: '#a32bff', tint: '#f5e9fe' },
  { color: '#fa7316', tint: '#fff0e5' },
  { color: '#12a37f', tint: '#e3f6f0' },
  { color: '#e8a10c', tint: '#fef3d9' },
  { color: '#e0479e', tint: '#fde8f3' },
  { color: '#0ca9c4', tint: '#dff5f9' },
  { color: '#64748b', tint: '#edf0f4' },
];
export const OTHER_COLOR = { color: '#8a8580', tint: '#f1f0ef' };
export const OTHER_LABEL = 'Other';

/** A count from chart data: a finite number ≥ 0, else 0 (a missing or garbled value never becomes NaN). */
export function toCount(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Whole-number percent of `part` in `whole`; 0 when there is no whole. */
export function percent(part, whole) {
  return whole > 0 ? Math.round((part / whole) * 100) : 0;
}

/** The labels and counts of a chart.js chartData, as parallel arrays (empty for anything unusable). */
export function readChartData(chartData) {
  const labels = chartData && Array.isArray(chartData.labels) ? chartData.labels : [];
  const sets = chartData && Array.isArray(chartData.datasets) ? chartData.datasets : [];
  const data = sets[0] && Array.isArray(sets[0].data) ? sets[0].data : [];
  return { labels: labels.map(l => String(l)), counts: labels.map((_l, i) => toCount(data[i])) };
}

/** The sum of the counts in a chartData — the same number TeamInsights.vue shows as `totalCalls`. */
export function chartTotal(chartData) {
  return readChartData(chartData).counts.reduce((a, b) => a + b, 0);
}

/**
 * Splits `slots` petals across `counts` by largest remainder, so the petals always add up to `slots`
 * (when there is anything to show), and every outcome with at least one follow-up keeps at least one petal.
 */
export function allocatePetals(counts, slots = PETALS) {
  const total = counts.reduce((a, b) => a + b, 0);
  if (!(total > 0) || !(slots > 0)) return counts.map(() => 0);
  const exact = counts.map(c => (c / total) * slots);
  const petals = exact.map(Math.floor);
  let left = slots - petals.reduce((a, b) => a + b, 0);
  exact
    .map((x, i) => ({ i, frac: x - Math.floor(x) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i)
    .forEach(({ i }) => { if (left > 0 && counts[i] > 0) { petals[i] += 1; left -= 1; } });
  // A tiny outcome must not vanish from the ring: lend it a petal from the biggest one.
  counts.forEach((c, i) => {
    if (c > 0 && petals[i] === 0) {
      let big = 0;
      petals.forEach((p, j) => { if (p > petals[big]) big = j; });
      if (petals[big] > 1) { petals[big] -= 1; petals[i] = 1; }
    }
  });
  return petals;
}

/**
 * The team card: { total, empty, outcomes: [{ key, label, count, percent, petals, color, tint, isOther }] }.
 * `key` is the outcome's slot in the colour list (or 'other'); `outcomes` holds at most MAX_OUTCOMES + 1 entries.
 * The "Other" entry also carries `folded: [{ label, count }]`, the outcomes it contains.
 */
export function buildOutcomeModel(chartData) {
  const { labels, counts } = readChartData(chartData);
  const total = counts.reduce((a, b) => a + b, 0);
  const outcomes = [];
  const folded = [];
  labels.forEach((label, i) => {
    if (i < MAX_OUTCOMES) outcomes.push({ key: i, label, count: counts[i], isOther: false, color: OUTCOME_COLORS[i].color, tint: OUTCOME_COLORS[i].tint });
    else folded.push({ label, count: counts[i] });
  });
  if (labels.length > MAX_OUTCOMES) {
    // `folded` keeps every outcome inside "Other" (its own label and count), so nothing is hidden from a tooltip or the data table.
    outcomes.push({ key: 'other', label: OTHER_LABEL, count: folded.reduce((a, f) => a + f.count, 0), isOther: true, color: OTHER_COLOR.color, tint: OTHER_COLOR.tint, folded });
  }
  const petals = allocatePetals(outcomes.map(o => o.count));
  outcomes.forEach((o, i) => { o.percent = percent(o.count, total); o.petals = petals[i]; });
  return { total, empty: total === 0, outcomes };
}

/**
 * The member list: one row per team member, sorted by follow-ups (most first, then name), bars on ONE shared scale
 * (the biggest member's bar is full width, everyone else is in proportion), each with their share of the team.
 *   members   [{ name, chartData }] — the same objects the classic per-member pies draw from
 *   teamModel buildOutcomeModel(team chartData) — fixes the colour of each outcome, by LABEL (a member's chartData can
 *             list outcomes in a different order, or carry one the team list lacks — that one counts as Other)
 * Returns { total, maxTotal, rows: [{ name, total, share, barPercent, segments: [{ label, count, percent, color }] }] }.
 */
export function buildMemberRows(members, teamModel) {
  const outcomes = (teamModel && teamModel.outcomes) || [];
  const byLabel = {};
  let other = null;
  outcomes.forEach((o) => { if (o.isOther) other = o; else if (!(o.label in byLabel)) byLabel[o.label] = o; });
  const otherSlot = () => other || { label: OTHER_LABEL, color: OTHER_COLOR.color, key: 'other' };

  const rows = (Array.isArray(members) ? members : []).map((m) => {
    const { labels, counts } = readChartData(m && m.chartData);
    const slots = {};
    labels.forEach((label, i) => {
      if (counts[i] <= 0) return;
      const slot = byLabel[label] || otherSlot();
      slots[slot.key] = slots[slot.key] || { slot, label: slot.label, count: 0 };
      slots[slot.key].count += counts[i];
    });
    const segments = Object.keys(slots).map(k => slots[k])
      .sort((a, b) => (a.slot.key === 'other') - (b.slot.key === 'other') || a.slot.key - b.slot.key)
      .map(s => ({ label: s.label, count: s.count, color: s.slot.color }));
    const total = segments.reduce((a, s) => a + s.count, 0);
    segments.forEach((s) => { s.percent = percent(s.count, total); });
    return { name: String((m && m.name) || ''), total, segments };
  });

  rows.sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
  const total = rows.reduce((a, r) => a + r.total, 0);
  const maxTotal = rows.reduce((a, r) => Math.max(a, r.total), 0);
  rows.forEach((r) => { r.share = percent(r.total, total); r.barPercent = maxTotal > 0 ? (r.total / maxTotal) * 100 : 0; });
  return { total, maxTotal, rows };
}

/**
 * A tidy value axis for a bar chart of counts: { top, step } with whole-number steps (1, 2, 5, 10, 20, 25, 50, 100 …), at most
 * five steps, and `top` the first multiple of `step` that is ≥ the biggest value (never below 4 — an empty chart still has an axis).
 */
export function niceAxis(maxValue) {
  const v = toCount(maxValue);
  if (v <= 4) return { top: 4, step: 1 };
  const base = [1, 2, 2.5, 5];
  let mag = 1;
  for (;;) {
    for (let i = 0; i < base.length; i += 1) {
      const step = base[i] * mag;
      if (Number.isInteger(step) && Math.ceil(v / step) <= 5) return { top: Math.max(Math.ceil(v / step), 2) * step, step };
    }
    mag *= 10;
  }
}

/**
 * One person's trend chart (the per-member bar charts on Month-to-Date and Custom Reports): the classic chartData is
 * { labels: [period…], datasets: [{ label: 'Call', data }, { label: 'Scheduled' | 'Received', data }] } (Patient Balances has only
 * the first dataset). `timeRange` is 'month', 'week' or anything else (= day) — only used to word the sentence.
 * Returns { buckets: [{ label, calls, second }], secondLabel, hasSecond, totalCalls, totalSecond, maxValue, busiest, insight }.
 */
export function buildTrendModel(chartData, timeRange) {
  const { labels, counts } = readChartData(chartData);
  const sets = chartData && Array.isArray(chartData.datasets) ? chartData.datasets : [];
  const secondSet = sets[1] && Array.isArray(sets[1].data) ? sets[1] : null;
  const secondLabel = secondSet && sets[1].label ? String(sets[1].label) : '';
  const buckets = labels.map((label, i) => ({ label, calls: counts[i], second: secondSet ? toCount(secondSet.data[i]) : 0 }));
  const totalCalls = buckets.reduce((a, b) => a + b.calls, 0);
  const totalSecond = buckets.reduce((a, b) => a + b.second, 0);
  const maxValue = buckets.reduce((a, b) => Math.max(a, b.calls, b.second), 0);
  let busiest = null;
  buckets.forEach((b) => { if (b.calls > 0 && (busiest === null || b.calls > busiest.calls)) busiest = b; });
  const word = timeRange === 'month' ? 'month' : timeRange === 'week' ? 'week' : 'day';
  let insight = 'No follow-ups in this period.';
  if (busiest) {
    // Only the busiest period: the scheduled / conversion numbers live in the stat tiles (the app's own Conversion Rate), so the sentence
    // never states a second, differently rounded percentage.
    insight = `${busiest.label} was the busiest ${word} with ${busiest.calls} ${busiest.calls === 1 ? 'follow-up' : 'follow-ups'}.`;
  }
  return { buckets, secondLabel, hasSecond: !!secondSet, totalCalls, totalSecond, maxValue, busiest, insight };
}

/** Initials for a member's avatar: first letters of the first two words ("Jane Q Smith" → "JQ"). */
export function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts.slice(0, 2).map(p => p.charAt(0).toUpperCase()).join('');
}
